import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n";
import { useSEO } from "../seo";
import { HERALDRY } from "../brand";

const COLLECTIONS = [
  {
    code: "sovereign",
    drop: "Drop 01",
    status: "live",
    nameEn: "The Sovereign",
    nameFr: "La Souveraine",
    yearEn: "Available now",
    yearFr: "Disponible",
    themeEn: "The founding collection. Black-on-black gothic streetwear. Hoodies, tees and silver heraldic jewelry hand-finished in Saint Barthélemy.",
    themeFr: "La collection fondatrice. Streetwear gothique noir sur noir. Sweats, tees et joaillerie héraldique argent finis main à Saint-Barthélemy.",
    image: "https://images.unsplash.com/photo-1551028719-00167b16eac5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    code: "vulgate",
    drop: "Drop 02",
    status: "coming",
    nameEn: "Vulgate",
    nameFr: "Vulgate",
    yearEn: "Coming 2026",
    yearFr: "Prochainement 2026",
    themeEn: "Sacred verses, woven in thread. Each piece embroidered with a verse drawn from the Latin Vulgate — the language of cathedrals and kings.",
    themeFr: "Versets sacrés, tissés dans le fil. Chaque pièce brodée d'un verset issu de la Vulgate latine — la langue des cathédrales et des rois.",
    image: "https://images.unsplash.com/photo-1507692049790-de58290a4334?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    code: "sanctuary",
    drop: "Drop 03",
    status: "coming",
    nameEn: "Sanctuary",
    nameFr: "Sanctuaire",
    yearEn: "Coming 2026",
    yearFr: "Prochainement 2026",
    themeEn: "An homage to the houses of worship. Stained glass colors, cathedral cuts, monastic silhouettes. Wear the architecture of the sacred.",
    themeFr: "Un hommage aux lieux de culte. Couleurs de vitraux, coupes cathédrales, silhouettes monastiques. Portez l'architecture du sacré.",
    image: "https://images.unsplash.com/photo-1548276145-69a9521f0499?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
];

export default function Collections() {
  const { lang } = useI18n();

  useSEO({
    title: lang === "fr" ? "Collections — Beaudoin" : "Collections — Beaudoin",
    description: lang === "fr"
      ? "Découvrez les collections actuelles et à venir de Beaudoin. Vulgate, Sanctuaire et la collection fondatrice Souveraine."
      : "Discover Beaudoin's current and upcoming collections. Vulgate, Sanctuary and the founding Sovereign collection.",
  });

  return (
    <div className="bg-black text-white pt-32 pb-32 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="mb-20 max-w-4xl"
        >
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-6">
            {lang === "fr" ? "Les Collections" : "The Collections"}
          </div>
          <h1 className="font-black text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tighter uppercase mb-8">
            {lang === "fr" ? (
              <>Drops <span className="text-blood">passés</span><br/>et à <span className="text-blood">venir</span>.</>
            ) : (
              <>Past and <span className="text-blood">future</span> drops.</>
            )}
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-2xl">
            {lang === "fr"
              ? "Chaque collection Beaudoin est pensée comme un manifeste. Pas de réassort. Pas de retour. Une fois épuisée, elle entre dans l'histoire de la maison."
              : "Every Beaudoin collection is conceived as a manifesto. No restock. No return. Once sold out, it enters the house's history."}
          </p>
        </motion.div>

        <div className="space-y-32">
          {COLLECTIONS.map((c, i) => (
            <motion.div
              key={c.code}
              data-testid={`collection-${c.code}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${
                i % 2 === 1 ? "md:flex-row-reverse" : ""
              }`}
            >
              <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="aspect-[4/5] overflow-hidden bg-[#0a0707] relative">
                  <img
                    src={c.image}
                    alt={c.nameEn}
                    className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                      c.status === "coming" ? "grayscale opacity-60" : ""
                    } hover:grayscale-0 hover:opacity-100 hover:scale-105`}
                  />
                  {c.status === "coming" && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="font-gothic text-7xl md:text-9xl text-blood/40 -rotate-12">
                        ✟
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-xs font-bold tracking-[0.3em] uppercase text-blood">
                    {c.drop}
                  </span>
                  <span className="w-12 h-px bg-blood" />
                  <span className={`text-xs font-bold tracking-[0.2em] uppercase ${
                    c.status === "live" ? "text-bone" : "text-white/40"
                  }`}>
                    {lang === "fr" ? c.yearFr : c.yearEn}
                  </span>
                </div>

                <h2 className="font-gothic text-blood text-6xl md:text-8xl leading-none mb-6">
                  {lang === "fr" ? c.nameFr : c.nameEn}
                </h2>

                <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
                  {lang === "fr" ? c.themeFr : c.themeEn}
                </p>

                {c.status === "live" ? (
                  <a
                    href="/shop"
                    data-testid={`collection-cta-${c.code}`}
                    className="inline-block bg-blood text-white px-10 py-5 text-xs font-bold tracking-[0.25em] uppercase hover:bg-blood-deep transition-colors"
                  >
                    {lang === "fr" ? "Acheter la collection" : "Shop the collection"}
                  </a>
                ) : (
                  <div
                    data-testid={`collection-notify-${c.code}`}
                    className="inline-block border border-white/30 text-white/60 px-10 py-5 text-xs font-bold tracking-[0.25em] uppercase"
                  >
                    {lang === "fr" ? "Bientôt — Inscription Drop Club" : "Coming soon — Join The Order"}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <section className="mt-40 border-t border-white/10 pt-20 text-center max-w-3xl mx-auto">
          <img src={HERALDRY.jerusalemCross} alt="" className="w-20 mx-auto mb-8 opacity-80" />
          <h3 className="font-gothic text-4xl md:text-6xl text-blood leading-none mb-6">
            {lang === "fr" ? "Soyez le premier" : "Be the first"}
          </h3>
          <p className="text-white/60 leading-relaxed mb-10 max-w-lg mx-auto">
            {lang === "fr"
              ? "Les prochains drops Vulgate et Sanctuaire seront annoncés en avant-première à nos abonnés. Aucun spam. Juste l'avis 48h avant tout le monde."
              : "The Vulgate and Sanctuary drops will be announced first to our subscribers. No spam. Just 48h heads-up before everyone else."}
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-black px-12 py-5 text-xs font-bold tracking-[0.3em] uppercase hover:bg-bone transition-colors"
          >
            {lang === "fr" ? "Rejoindre The Order" : "Join The Order"}
          </a>
        </section>
      </section>
    </div>
  );
}
